import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoadScript, GoogleMap, Marker, Autocomplete } from '@react-google-maps/api';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import Icon from '../components/Icon';
import PaymentMethodsForm from '../components/PaymentMethodsForm';
import {
  getRestaurant,
  createRestaurant,
  updateRestaurant,
  deleteRestaurant,
  getRestaurantUsers,
  assignUsersToRestaurant,
  listOrganizationUsers,
} from '../services/api';
import { useBreadcrumb } from '../context/BreadcrumbContext';
import { useAuth } from '../context/AuthContext';
import { trackEvent } from '../utils/analytics';
import { storage } from '../utils/storage';
import './RestaurantFormPage.css';

const libraries = ['places'];
const mapContainerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px',
};

const TABS = [
  { id: 'general', labelKey: 'restaurantForm.tabs.general', icon: 'store' },
  { id: 'location', labelKey: 'restaurantForm.tabs.location', icon: 'location_on' },
  { id: 'hours', labelKey: 'restaurantForm.tabs.hours', icon: 'schedule' },
  { id: 'printing', labelKey: 'restaurantForm.tabs.printing', icon: 'print' },
  { id: 'branding', labelKey: 'restaurantForm.tabs.branding', icon: 'palette' },
  { id: 'users', labelKey: 'restaurantForm.tabs.users', icon: 'group' },
];

function RestaurantFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { restaurantId } = useParams();
  const isEditMode = !!restaurantId;
  const { setDynamicLabel } = useBreadcrumb();
  const { organization } = useAuth();
  // Defaults org-level de métodos de pago (NER-1284): proyectados en
  // GET /organizations/me. `null` = la org no tiene defaults configurados.
  const orgPaymentDefaults = organization?.paymentMethodDefaults || null;

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(location.state?.message || '');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [restaurantData, setRestaurantData] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [fieldErrors, setFieldErrors] = useState({});

  // Deep-link a una tab vía hash (#location, #hours, ...) — usado por el CTA
  // "Configurar ubicación" del form de delivery en Settings (NER-1272).
  useEffect(() => {
    const tabId = location.hash.replace('#', '');
    if (tabId && TABS.some((tab) => tab.id === tabId)) {
      setActiveTab(tabId);
    }
  }, [location.hash]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    phone: '',
    email: '',
    timezone: 'America/Argentina/Buenos_Aires',
    currency: 'ARS',
    address: '',
    wifiNetwork: '',
    wifiPassword: '',
    businessHours: null,
    paymentMethods: undefined,
    printSettings: {
      headerText: '',
      footerText: '',
      showLogo: true,
      kitchenFontSize: 'medium',
      autoPrintKitchen: false,
      autoPrintCustomer: false,
    },
    branding: {
      logoUrl: '',
      tagline: '',
      colors: {
        primary: '#7e4fb9',
        secondary: '#FF6B35',
        accent: '#E91E63',
      },
      fonts: {
        primary: 'Inter',
      },
      buttonStyle: 'rounded',
      cardStyle: 'elevated',
      darkModeEnabled: true,
    },
  });

  // Users state
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Google Maps state
  const [mapCenter, setMapCenter] = useState({ lat: -34.6037, lng: -58.3816 }); // Buenos Aires default
  const [markerPosition, setMarkerPosition] = useState(null);
  const autocompleteRef = useRef(null);

  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const handleNameBlur = () => {
    if (!formData.slug && formData.name) {
      setFormData((prev) => ({ ...prev, slug: generateSlug(prev.name) }));
    }
  };

  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData({ ...formData, slug: value });
  };

  useEffect(() => {
    if (isEditMode) {
      loadRestaurant();
    }
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  useEffect(() => {
    if (isEditMode && formData.name) setDynamicLabel(formData.name);
  }, [isEditMode, formData.name, setDynamicLabel]);

  const loadRestaurant = async () => {
    try {
      setLoading(true);
      const data = await getRestaurant(restaurantId);
      console.log('Restaurant data loaded:', data);
      setRestaurantData(data);
      setFormData({
        name: data.name || '',
        slug: data.slug || '',
        address: data.address || '',
        phone: data.phone || '',
        email: data.email || '',
        timezone: data.timezone || 'America/Argentina/Buenos_Aires',
        currency: data.currency || 'ARS',
        wifiNetwork: data.wifiNetwork || '',
        wifiPassword: data.wifiPassword || '',
        businessHours: data.businessHours || null,
        paymentMethods: data.paymentMethods,
        printSettings: data.printSettings || {
          headerText: '',
          footerText: '',
          showLogo: true,
          kitchenFontSize: 'medium',
          autoPrintKitchen: false,
          autoPrintCustomer: false,
        },
        branding: {
          ...(data.branding || {}),
          logoUrl: data.branding?.logoUrl || '',
          tagline: data.branding?.tagline || '',
          colors: {
            ...(data.branding?.colors || {}),
            primary: data.branding?.colors?.primary || '#7e4fb9',
            secondary: data.branding?.colors?.secondary || '#FF6B35',
            accent: data.branding?.colors?.accent || '#E91E63',
          },
          fonts: {
            ...(data.branding?.fonts || {}),
            primary: data.branding?.fonts?.primary || 'Inter',
          },
          buttonStyle: data.branding?.buttonStyle || 'rounded',
          cardStyle: data.branding?.cardStyle || 'elevated',
          darkModeEnabled: data.branding?.darkModeEnabled !== false,
        },
      });

      if (data.location?.lat && data.location?.lng) {
        const location = {
          lat: parseFloat(data.location.lat),
          lng: parseFloat(data.location.lng),
        };
        setMapCenter(location);
        setMarkerPosition(location);
      }
    } catch (error) {
      console.error('Error loading restaurant:', error);
      setMessage('Error al cargar restaurante');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const organizationId = storage.get('selectedOrganizationId');
      if (!organizationId) return;

      if (isEditMode && restaurantId) {
        // Modo edición: obtener usuarios con información de acceso al restaurante
        const data = await getRestaurantUsers(organizationId, restaurantId);
        setUsers(data.users || []);

        // Pre-seleccionar usuarios que ya tienen acceso (excluyendo OWNERs)
        const usersWithAccess = data.users
          .filter((user) => user.hasAccess && user.accessType !== 'all')
          .map((user) => user.userId);
        setSelectedUserIds(usersWithAccess);
      } else {
        // Modo creación: obtener todos los usuarios de la organización
        const data = await listOrganizationUsers(organizationId);
        // Adaptar formato para que coincida con getRestaurantUsers
        const formattedUsers = data.users.map((user) => ({
          ...user,
          hasAccess: false,
          accessType: user.role === 'owner' ? 'all' : 'specific',
        }));
        setUsers(formattedUsers);
        setSelectedUserIds([]);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();

      if (place.geometry && place.geometry.location) {
        const location = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };

        setFormData({ ...formData, address: place.formatted_address || place.name });
        setMapCenter(location);
        setMarkerPosition(location);
      }
    }
  };

  const onMapClick = async (e) => {
    const location = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setMarkerPosition(location);
    setMapCenter(location);

    // Reverse geocoding para obtener la dirección del punto clickeado
    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location });

      if (response.results && response.results[0]) {
        const address = response.results[0].formatted_address;
        setFormData((prev) => ({ ...prev, address }));
      }
    } catch (error) {
      console.error('Error en reverse geocoding:', error);
      // No bloquear el flujo si falla el geocoding
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!formData.name.trim()) {
      errors.name = t('restaurantForm.validation.nameRequired');
    }
    if (!formData.address.trim()) {
      errors.address = t('restaurantForm.validation.addressRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      // Redirect to the first tab with an error (name first, then address)
      if (errors.name) {
        setActiveTab('general');
      } else if (errors.address) {
        setActiveTab('location');
      }
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      // Auto-generate derived color values from primary
      const brandingToSend = formData.branding ? { ...formData.branding } : undefined;
      if (brandingToSend?.colors?.primary) {
        const hex = brandingToSend.colors.primary.replace('#', '');
        const num = parseInt(hex, 16);
        const r = (num >> 16) & 255;
        const g = (num >> 8) & 255;
        const b = num & 255;
        const darken = (c) => Math.max(0, c - 26); // ~10% darker
        brandingToSend.colors = {
          ...brandingToSend.colors,
          primaryHover: `#${((1 << 24) + (darken(r) << 16) + (darken(g) << 8) + darken(b)).toString(16).slice(1)}`,
          primaryLight: `rgba(${r}, ${g}, ${b}, 0.1)`,
        };
      }

      const dataToSend = {
        ...formData,
        branding: brandingToSend,
        location: markerPosition
          ? {
              lat: markerPosition.lat.toString(),
              lng: markerPosition.lng.toString(),
            }
          : undefined,
      };

      let savedRestaurantId = restaurantId;

      if (isEditMode) {
        await updateRestaurant(restaurantId, dataToSend);
      } else {
        const createResponse = await createRestaurant(dataToSend);
        savedRestaurantId = createResponse.restaurant?.restaurantId;
        trackEvent('restaurant_created', {
          organization_id: storage.get('selectedOrganizationId'),
          restaurant_id: savedRestaurantId,
        });
      }

      // Asignar usuarios al restaurante (solo si tenemos usuarios seleccionados)
      if (savedRestaurantId && selectedUserIds.length > 0) {
        const organizationId = storage.get('selectedOrganizationId');
        if (organizationId) {
          try {
            await assignUsersToRestaurant(organizationId, savedRestaurantId, selectedUserIds);
          } catch (userError) {
            console.error('Error assigning users:', userError);
            // No fallar el flujo completo si falla la asignación de usuarios
          }
        }
      }

      navigate('/restaurant');
    } catch (error) {
      console.error('Error saving restaurant:', error);
      setMessage('Error al guardar restaurante');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/restaurant');
  };

  const handleToggleStatus = async () => {
    if (!restaurantData) return;

    try {
      setSaving(true);
      const newStatus = restaurantData.status === 'active' ? 'inactive' : 'active';
      await updateRestaurant(restaurantId, { status: newStatus });

      // Actualizar el estado local
      setRestaurantData({ ...restaurantData, status: newStatus });
      setMessage(`Restaurante ${newStatus === 'active' ? 'activado' : 'desactivado'} exitosamente`);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error updating restaurant status:', error);
      setMessage('Error al actualizar estado del restaurante');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      await deleteRestaurant(restaurantId);
      closeDeleteModal();
      navigate('/restaurant');
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      setMessage('Error al eliminar restaurante');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Cargando restaurante...</div>;
  }

  return (
    <div className="restaurant-form-page">
      <div className="form-header">
        <div>
          <h1>{isEditMode ? 'Editar Restaurante' : 'Nuevo Restaurante'}</h1>
          <p>
            {isEditMode
              ? 'Actualiza la información del restaurante'
              : 'Completa la información para crear un nuevo restaurante'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="restaurant-form">
        <div className="form-tabs">
          {TABS.map((tab) => {
            const hasIndicator = tab.id === 'general' || tab.id === 'location';
            const fieldKey =
              tab.id === 'general' ? 'name' : tab.id === 'location' ? 'address' : null;
            const fieldValue = fieldKey ? formData[fieldKey]?.trim() : null;
            const isValid = fieldKey ? !!fieldValue : null;

            return (
              <button
                key={tab.id}
                type="button"
                className={`form-tab ${activeTab === tab.id ? 'form-tab--active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon name={tab.icon} size="small" />
                {t(tab.labelKey)}
                {hasIndicator && (
                  <span
                    className={`form-tab-indicator ${isValid ? 'form-tab-indicator--valid' : 'form-tab-indicator--warning'}`}
                  >
                    <Icon name={isValid ? 'check_circle' : 'warning'} size="small" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="form-tab-content">
          {/* Tab: General */}
          {activeTab === 'general' && (
            <>
              <div className="form-section">
                <h2>Información Básica</h2>

                <div className="form-group">
                  <label>{t('restaurantForm.validation.nameLabel')} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({ ...formData, name: e.target.value });
                      if (fieldErrors.name)
                        setFieldErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                    onBlur={handleNameBlur}
                    placeholder="Ej: La Trattoria"
                    className={fieldErrors.name ? 'input-error' : ''}
                  />
                  {fieldErrors.name && <span className="field-error">{fieldErrors.name}</span>}
                </div>

                <div className="form-group">
                  <label>{t('restaurantForm.slugLabel')}</label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={handleSlugChange}
                    placeholder={t('restaurantForm.slugPlaceholder')}
                  />
                  {formData.slug && (
                    <small className="form-hint">
                      {t('restaurantForm.slugPreview')}: menu.nerdo.solutions/
                      <strong>{formData.slug}</strong>/mesa/1
                    </small>
                  )}
                </div>

                <div className="form-group">
                  <label>Teléfono</label>
                  <PhoneInput
                    country={'ar'}
                    value={formData.phone}
                    onChange={(phone) => setFormData({ ...formData, phone: phone })}
                    inputProps={{
                      required: false,
                    }}
                    containerClass="phone-input-container"
                    inputClass="phone-input"
                    buttonClass="phone-input-button"
                    preferredCountries={['ar', 'uy', 'cl', 'br']}
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contacto@restaurante.com"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>WiFi - Nombre de Red</label>
                    <input
                      type="text"
                      value={formData.wifiNetwork}
                      onChange={(e) => setFormData({ ...formData, wifiNetwork: e.target.value })}
                      placeholder="Ej: MiRestaurante_WiFi"
                    />
                  </div>
                  <div className="form-group">
                    <label>WiFi - Contraseña</label>
                    <input
                      type="password"
                      value={formData.wifiPassword}
                      onChange={(e) => setFormData({ ...formData, wifiPassword: e.target.value })}
                      placeholder="Contraseña de la red"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Zona Horaria</label>
                    <select
                      value={formData.timezone}
                      onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    >
                      <option value="America/Argentina/Buenos_Aires">
                        Argentina - Buenos Aires
                      </option>
                      <option value="America/Montevideo">Uruguay - Montevideo</option>
                      <option value="America/Santiago">Chile - Santiago</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Moneda</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    >
                      <option value="ARS">ARS - Peso Argentino</option>
                      <option value="USD">USD - Dólar</option>
                      <option value="UYU">UYU - Peso Uruguayo</option>
                      <option value="CLP">CLP - Peso Chileno</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <PaymentMethodsForm
                  paymentMethods={formData.paymentMethods}
                  orgDefaults={orgPaymentDefaults}
                  onChange={(newMethods) =>
                    setFormData({ ...formData, paymentMethods: newMethods })
                  }
                />
              </div>
            </>
          )}

          {/* Tab: Ubicación */}
          {activeTab === 'location' && (
            <div className="form-section">
              <h2>Ubicación</h2>

              <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={libraries}>
                <div className="form-group">
                  <label>{t('restaurantForm.validation.addressLabel')} *</label>
                  <Autocomplete
                    onLoad={(autocomplete) => (autocompleteRef.current = autocomplete)}
                    onPlaceChanged={onPlaceChanged}
                    options={{
                      componentRestrictions: { country: 'ar' },
                      fields: ['formatted_address', 'geometry', 'name'],
                    }}
                  >
                    <input
                      type="text"
                      value={formData.address}
                      onChange={(e) => {
                        setFormData({ ...formData, address: e.target.value });
                        if (fieldErrors.address)
                          setFieldErrors((prev) => ({ ...prev, address: undefined }));
                      }}
                      placeholder="Buscar dirección..."
                      className={fieldErrors.address ? 'input-error' : ''}
                    />
                  </Autocomplete>
                  {fieldErrors.address && (
                    <span className="field-error">{fieldErrors.address}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Ubicación en el mapa</label>
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={mapCenter}
                    zoom={markerPosition ? 15 : 12}
                    onClick={onMapClick}
                    options={{
                      gestureHandling: 'greedy',
                      scrollwheel: false,
                    }}
                  >
                    {markerPosition && <Marker position={markerPosition} />}
                  </GoogleMap>
                  <small className="form-hint">
                    Haz clic en el mapa para ajustar la ubicación exacta
                  </small>
                </div>
              </LoadScript>
            </div>
          )}

          {/* Tab: Horarios */}
          {activeTab === 'hours' && (
            <div className="form-section">
              <h2>Horarios de Atención</h2>
              <p className="form-description">
                Configura los horarios de apertura y cierre. Podés agregar múltiples turnos por día
                (ej: mediodía y noche).
              </p>
              {(() => {
                const DAYS = [
                  { key: 'monday', label: 'Lunes' },
                  { key: 'tuesday', label: 'Martes' },
                  { key: 'wednesday', label: 'Miércoles' },
                  { key: 'thursday', label: 'Jueves' },
                  { key: 'friday', label: 'Viernes' },
                  { key: 'saturday', label: 'Sábado' },
                  { key: 'sunday', label: 'Domingo' },
                ];
                const hours = formData.businessHours || {};

                // Normalize: support both old format {open,close} and new [{open,close}]
                const getRanges = (day) => {
                  const val = hours[day];
                  if (!val) return null;
                  if (Array.isArray(val)) return val;
                  if (val.open) return [val]; // migrate old single-range format
                  return null;
                };

                const setDayRanges = (day, ranges) => {
                  setFormData({
                    ...formData,
                    businessHours: { ...hours, [day]: ranges },
                  });
                };

                const toggleDay = (day) => {
                  const current = getRanges(day);
                  setDayRanges(day, current ? null : [{ open: '09:00', close: '22:00' }]);
                };

                const updateRange = (day, index, field, value) => {
                  const ranges = [...(getRanges(day) || [])];
                  ranges[index] = { ...ranges[index], [field]: value };
                  setDayRanges(day, ranges);
                };

                const addRange = (day) => {
                  const ranges = [...(getRanges(day) || [])];
                  ranges.push({ open: '20:00', close: '00:00' });
                  setDayRanges(day, ranges);
                };

                const removeRange = (day, index) => {
                  const ranges = (getRanges(day) || []).filter((_, i) => i !== index);
                  setDayRanges(day, ranges.length > 0 ? ranges : null);
                };

                const copyToAll = () => {
                  const first = DAYS.find((d) => getRanges(d.key));
                  if (!first) return;
                  const source = getRanges(first.key);
                  const copied = {};
                  DAYS.forEach((d) => {
                    copied[d.key] = source ? source.map((r) => ({ ...r })) : null;
                  });
                  setFormData({ ...formData, businessHours: copied });
                };

                return (
                  <>
                    <div className="business-hours-list">
                      {DAYS.map((day) => {
                        const ranges = getRanges(day.key);
                        const isOpen = !!ranges;
                        return (
                          <div key={day.key} className="business-hours-row">
                            <div className="business-hours-row-header">
                              <label className="business-hours-day">{day.label}</label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={isOpen}
                                  onChange={() => toggleDay(day.key)}
                                  id={`hours-toggle-${day.key}`}
                                />
                                <label
                                  htmlFor={`hours-toggle-${day.key}`}
                                  style={{
                                    margin: 0,
                                    fontSize: '13px',
                                    color: 'var(--text-secondary)',
                                  }}
                                >
                                  {isOpen ? 'Abierto' : 'Cerrado'}
                                </label>
                              </div>
                            </div>
                            {isOpen && (
                              <div className="business-hours-ranges">
                                {ranges.map((range, idx) => (
                                  <div key={idx} className="business-hours-times">
                                    <input
                                      type="time"
                                      value={range.open || '09:00'}
                                      onChange={(e) =>
                                        updateRange(day.key, idx, 'open', e.target.value)
                                      }
                                    />
                                    <span>—</span>
                                    <input
                                      type="time"
                                      value={range.close || '22:00'}
                                      onChange={(e) =>
                                        updateRange(day.key, idx, 'close', e.target.value)
                                      }
                                    />
                                    {ranges.length > 1 && (
                                      <button
                                        type="button"
                                        className="business-hours-remove"
                                        onClick={() => removeRange(day.key, idx)}
                                        title="Quitar turno"
                                      >
                                        ×
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <button
                                  type="button"
                                  className="business-hours-add-range"
                                  onClick={() => addRange(day.key)}
                                >
                                  + Agregar turno
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={copyToAll}
                      style={{ marginTop: '12px' }}
                    >
                      Copiar primer horario a todos los días
                    </button>
                  </>
                );
              })()}
            </div>
          )}

          {/* Tab: Impresión */}
          {activeTab === 'printing' && (
            <div className="form-section">
              <h2>Configuración de Impresión</h2>
              <div className="form-group">
                <label>Texto de Cabecera (Ticket Cliente)</label>
                <textarea
                  value={formData.printSettings?.headerText || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      printSettings: { ...formData.printSettings, headerText: e.target.value },
                    })
                  }
                  placeholder="Ej: ¡Gracias por su visita!"
                  rows={2}
                  className="form-control"
                />
              </div>
              <div className="form-group">
                <label>Texto de Pie de Página (Ticket Cliente)</label>
                <textarea
                  value={formData.printSettings?.footerText || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      printSettings: { ...formData.printSettings, footerText: e.target.value },
                    })
                  }
                  placeholder="Ej: Síguenos en Instagram @restaurante"
                  rows={2}
                  className="form-control"
                />
              </div>

              <div className="form-row">
                <div
                  className="form-group checkbox-group"
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <input
                    type="checkbox"
                    id="showLogo"
                    checked={formData.printSettings?.showLogo || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        printSettings: { ...formData.printSettings, showLogo: e.target.checked },
                      })
                    }
                  />
                  <label htmlFor="showLogo" style={{ margin: 0 }}>
                    Mostrar Logo en Ticket
                  </label>
                </div>
                <div className="form-group">
                  <label>Tamaño Fuente Cocina</label>
                  <select
                    value={formData.printSettings?.kitchenFontSize || 'medium'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        printSettings: {
                          ...formData.printSettings,
                          kitchenFontSize: e.target.value,
                        },
                      })
                    }
                  >
                    <option value="small">Pequeño</option>
                    <option value="medium">Mediano</option>
                    <option value="large">Grande</option>
                    <option value="extra-large">Extra Grande</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div
                  className="form-group checkbox-group"
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <input
                    type="checkbox"
                    id="autoPrintKitchen"
                    checked={formData.printSettings?.autoPrintKitchen || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        printSettings: {
                          ...formData.printSettings,
                          autoPrintKitchen: e.target.checked,
                        },
                      })
                    }
                  />
                  <label htmlFor="autoPrintKitchen" style={{ margin: 0 }}>
                    Auto-imprimir Comanda (Cocina)
                  </label>
                </div>
                <div
                  className="form-group checkbox-group"
                  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                >
                  <input
                    type="checkbox"
                    id="autoPrintCustomer"
                    checked={formData.printSettings?.autoPrintCustomer || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        printSettings: {
                          ...formData.printSettings,
                          autoPrintCustomer: e.target.checked,
                        },
                      })
                    }
                  />
                  <label htmlFor="autoPrintCustomer" style={{ margin: 0 }}>
                    Auto-imprimir Cuenta (Cliente)
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Tab: Marca */}
          {activeTab === 'branding' && (
            <div className="form-section">
              <h2>{t('restaurantForm.brandingTitle')}</h2>

              <div className="form-group">
                <label>Logo URL</label>
                <input
                  type="text"
                  value={formData.branding?.logoUrl || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      branding: { ...formData.branding, logoUrl: e.target.value },
                    })
                  }
                  placeholder="https://ejemplo.com/logo.png"
                />
              </div>

              <div className="form-group">
                <label>{t('restaurantForm.taglineLabel')}</label>
                <input
                  type="text"
                  value={formData.branding?.tagline || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      branding: { ...formData.branding, tagline: e.target.value },
                    })
                  }
                  placeholder="Ej: La mejor cocina de la ciudad"
                />
              </div>

              <div className="form-row" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                <div className="form-group" style={{ flex: 1, minWidth: '150px' }}>
                  <label>Color Primario</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.branding?.colors?.primary || '#7e4fb9'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          branding: {
                            ...formData.branding,
                            colors: { ...formData.branding?.colors, primary: e.target.value },
                          },
                        })
                      }
                      style={{ width: '45px', height: '36px', padding: '2px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.branding?.colors?.primary || '#7e4fb9'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          branding: {
                            ...formData.branding,
                            colors: { ...formData.branding?.colors, primary: e.target.value },
                          },
                        })
                      }
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: '150px' }}>
                  <label>Color Secundario</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.branding?.colors?.secondary || '#FF6B35'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          branding: {
                            ...formData.branding,
                            colors: { ...formData.branding?.colors, secondary: e.target.value },
                          },
                        })
                      }
                      style={{ width: '45px', height: '36px', padding: '2px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.branding?.colors?.secondary || '#FF6B35'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          branding: {
                            ...formData.branding,
                            colors: { ...formData.branding?.colors, secondary: e.target.value },
                          },
                        })
                      }
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
                <div className="form-group" style={{ flex: 1, minWidth: '150px' }}>
                  <label>Color Acento</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={formData.branding?.colors?.accent || '#E91E63'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          branding: {
                            ...formData.branding,
                            colors: { ...formData.branding?.colors, accent: e.target.value },
                          },
                        })
                      }
                      style={{ width: '45px', height: '36px', padding: '2px', cursor: 'pointer' }}
                    />
                    <input
                      type="text"
                      value={formData.branding?.colors?.accent || '#E91E63'}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          branding: {
                            ...formData.branding,
                            colors: { ...formData.branding?.colors, accent: e.target.value },
                          },
                        })
                      }
                      style={{ flex: 1 }}
                    />
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Tipografía Principal</label>
                <select
                  value={formData.branding?.fonts?.primary || 'Inter'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      branding: {
                        ...formData.branding,
                        fonts: { ...formData.branding?.fonts, primary: e.target.value },
                      },
                    })
                  }
                >
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Poppins">Poppins</option>
                  <option value="Montserrat">Montserrat</option>
                  <option value="Playfair Display">Playfair Display</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Lato">Lato</option>
                  <option value="Nunito">Nunito</option>
                </select>
              </div>

              <div className="form-row" style={{ display: 'flex', gap: '15px' }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Estilo de Botones</label>
                  <select
                    value={formData.branding?.buttonStyle || 'rounded'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        branding: { ...formData.branding, buttonStyle: e.target.value },
                      })
                    }
                  >
                    <option value="rounded">Redondeado</option>
                    <option value="pill">Pill</option>
                    <option value="square">Cuadrado</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Estilo de Tarjetas</label>
                  <select
                    value={formData.branding?.cardStyle || 'elevated'}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        branding: { ...formData.branding, cardStyle: e.target.value },
                      })
                    }
                  >
                    <option value="elevated">Elevada</option>
                    <option value="flat">Plana</option>
                    <option value="outlined">Con borde</option>
                  </select>
                </div>
              </div>

              <div
                className="form-group checkbox-group"
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <input
                  type="checkbox"
                  id="darkModeEnabled"
                  checked={formData.branding?.darkModeEnabled !== false}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      branding: { ...formData.branding, darkModeEnabled: e.target.checked },
                    })
                  }
                />
                <label htmlFor="darkModeEnabled" style={{ margin: 0 }}>
                  Permitir modo oscuro a clientes
                </label>
              </div>
            </div>
          )}

          {/* Tab: Usuarios */}
          {activeTab === 'users' && (
            <div className="form-section">
              <h2>{t('restaurantForm.usersWithAccess')}</h2>
              <p className="form-description">{t('restaurantForm.usersAccessDescription')}</p>

              {loadingUsers ? (
                <p>{t('restaurantForm.loadingUsers')}</p>
              ) : users.length === 0 ? (
                <p className="empty-message">{t('restaurantForm.noUsersAvailable')}</p>
              ) : (
                <div className="users-grid">
                  {users
                    .filter((user) => user.accessType !== 'all')
                    .map((user) => (
                      <label key={user.userId} className="user-checkbox-card">
                        <input
                          type="checkbox"
                          checked={selectedUserIds.includes(user.userId)}
                          onChange={() => handleUserToggle(user.userId)}
                        />
                        <div className="user-info">
                          <div className="user-name">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="user-email">{user.email}</div>
                          <div className="user-role-badge">
                            {t(`users.${user.role.toLowerCase()}`, user.role)}
                          </div>
                        </div>
                      </label>
                    ))}
                </div>
              )}

              {users.filter(
                (user) => user.accessType === 'all' && (user.firstName || user.lastName)
              ).length > 0 && (
                <div className="owner-info">
                  <strong>{t('restaurantForm.autoAccessOwners')}</strong>
                  <ul>
                    {users
                      .filter(
                        (user) => user.accessType === 'all' && (user.firstName || user.lastName)
                      )
                      .map((user) => {
                        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
                        return (
                          <li key={user.userId}>
                            {fullName} ({user.email})
                          </li>
                        );
                      })}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <div className="form-actions">
          <div className="form-actions-left">
            {isEditMode && restaurantData && restaurantData.restaurantId && (
              <>
                <button
                  type="button"
                  className={`btn ${restaurantData.status === 'active' ? 'btn-warning' : 'btn-success'}`}
                  onClick={handleToggleStatus}
                  disabled={saving}
                  title={
                    restaurantData.status === 'active'
                      ? 'Desactivar restaurante'
                      : 'Activar restaurante'
                  }
                >
                  <Icon
                    name={restaurantData.status === 'active' ? 'cancel' : 'check_circle'}
                    size="small"
                  />
                  {restaurantData.status === 'active' ? 'Desactivar' : 'Activar'}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={openDeleteModal}
                  disabled={saving}
                >
                  <Icon name="delete" size="small" />
                  Eliminar
                </button>
              </>
            )}
          </div>
          <div className="form-actions-right">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={handleCancel}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`btn btn-primary ${!isEditMode && (!formData.name.trim() || !formData.address.trim()) ? 'btn-primary--incomplete' : ''}`}
              disabled={saving}
            >
              {saving ? 'Guardando...' : isEditMode ? 'Guardar Cambios' : 'Crear Restaurante'}
            </button>
          </div>
        </div>
      </form>

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal-overlay" onClick={closeDeleteModal}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar eliminación</h2>
              <button type="button" className="close-button" onClick={closeDeleteModal}>
                ×
              </button>
            </div>

            <div className="modal-body">
              <p>
                ¿Estás seguro de que deseas eliminar el restaurante{' '}
                <strong>{restaurantData?.name}</strong>?
              </p>
              <p className="warning-text">
                Esta acción no se puede deshacer. El restaurante será marcado como eliminado.
              </p>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={closeDeleteModal}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleDelete}
                disabled={saving}
              >
                {saving ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RestaurantFormPage;
